import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter, Inject,
  Input, OnDestroy, OnInit, Optional,
  Output,
  TemplateRef,
  ViewChild
} from "@angular/core";
import {BehaviorSubject, fromEvent, merge, Observable} from "rxjs";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {DROPDOWN_ANIMATION} from "../../animations";
import {KEYBOARD_KEY_PRESSED} from "../../providers/keyboardEvents.provider";
import {DestroyStreamService} from "../../services";
import {takeUntil} from "rxjs/operators";
import {IntInputComponent} from "../int-input/int-input.component";
import {IntPortalOutletService} from "../../modules/portal-outlet/int-portal-outlet.service";
import {WINDOW_REF_TOKEN} from "../../tokens";

@Component({
  selector: 'int-select',
  templateUrl: 'int-select.component.html',
  styleUrls: ['./int-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService,
    IntInputComponent,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntSelectComponent,
      multi: true,
    },
  ],
  animations: [DROPDOWN_ANIMATION]
})
export class IntSelectComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {

  label$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  value$ = new BehaviorSubject<string>('');
  isFocus$ = new BehaviorSubject<boolean>(false);
  list$ = new BehaviorSubject<Array<{label: string; value: any}> | null>([]);
  id!: number;
  animation$ = new BehaviorSubject(false);

  @Input()
  set list(payload: Array<{label: string; value: any}> | null) {
    this.list$.next(payload);
  };

  @Input()
  set value(value) {
    this.label$.next(value);
  }

  get value(): string {
    return this.label$.value;
  }

  @Input()
  placeholder = '';

  @Input()
  label = '';

  @Input() defaultHeight = '50px';
  @Input() defaultHeightPriority = false

  @Input() dropdownWidth: number = 0;
  @Input() dropdownHeight: string = '10rem';

  @Input()
  verticalOffset: number = 0;

  @Input()
  verticalAlign: 'top' | 'bottom' = 'top';

  @Input()
  showLabelTip = true;

  @Input()
  readonly = true;

  @Input()
  emptyListPlaceholder = '';

  @Output()
  changeValueEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  focusEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  disabled = false;
  touched = false;
  focused = false;

  @ViewChild('rootElement', { static: false })
  private readonly rootElement!: ElementRef;

  @ViewChild(IntInputComponent) intInput!:IntInputComponent

  @ViewChild('droptest') droptest!: TemplateRef<any>

  constructor(
    @Inject(KEYBOARD_KEY_PRESSED)
    private readonly keyStream$: Observable<string>,
    private readonly destroy$: DestroyStreamService,
    @Optional()
    private readonly portal: IntPortalOutletService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(WINDOW_REF_TOKEN) private readonly windowRef: Window
  ) {

    merge(
      fromEvent(this.windowRef, 'scroll')
    ).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.intInput.setBlur();
      this.isFocus$.next(false);
    })
  }

  get isOptionsExist(): boolean {
    return (this.list$.value || []).length > 0
  }

  ngAfterViewInit() {

  }

  ngOnDestroy() {
    this.closeDropdown()
  }

  ngOnInit() {

    this.id = Math.random();

    this.keyStream$.pipe(takeUntil(this.destroy$)).subscribe((key) => {
      if(key === 'Escape') {
        this.intInput.setBlur();
        this.isFocus$.next(false);
      }
    });

    this.list$.pipe(takeUntil(this.destroy$)).subscribe(_ => {
      const item: {label: string; value: any} | undefined =
        this.list$.value?.find(item => item.value === this.value$.value);
      if(item) {
        this.label$.next(item?.label);
      }
    })

    this.isFocus$.pipe(takeUntil(this.destroy$)).subscribe((status): void => {
      const coords = this.rootElement?.nativeElement.getBoundingClientRect();
      if(!coords) {
        return undefined;
      }else{
        const { x, y, width, height } = coords;
        if (status) {
          this.animation$.next(true);
          this.portal.setTemplate(this.droptest, `droptest--${this.id}`,
            {
              x,
              y,
              height,
              width: this.dropdownWidth || width,
              isFocus: status,
              currentValue: this.value$.value
            });
        } else {
          this.closeDropdown();
        }
      }
    })
  }

  closeDropdown() {
    this.animation$.next(false);
    setTimeout(() => {
      this.portal.removeTemplate(`droptest--${this.id}`);
    }, 200)
  }

  toggleField() {
    this.isFocus$.next(!this.isFocus$.value);
    if(!this.isFocus$.value) {
      this.intInput.setBlur();
    }else{
      this.intInput.setFocus();
    }
  }

  writeValue(value: string) {
    const item: {label: string; value: any} | undefined = this.list$.value?.find(item => item.value === value);
    if(item) {
      this.label$.next(item?.label);
      this.value$.next(item?.value);
    }else{
      this.label$.next(value);
      this.value$.next(value);
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  onChange(val: any) {}

  onTouched() {}

  handleChange(payload: {label: string; value: any}) {
    this.label$.next(payload.label);
    this.onChange(payload.value);
    this.value$.next(payload.value);
    this.changeValueEvent.emit(payload.value);
  }
  focusHandler(status: boolean) {
    this.focusEvent.emit(status);
    if(!status) {
      setTimeout(() => {
        this.isFocus$.next(status);
      }, 100)
    }else{
      this.isFocus$.next(status);
    }
  }
  onBlur(status: boolean) {

  }
}
